import { useEffect, useState } from 'react';
import getFilteredRestaurantsCarousel, {
  type FilteredRestaurant,
  type FilteredRestaurantsCarouselMode,
} from '@discover-data/gql/getters/filteredRestaurantsCarousel';
import Logger from '@/lib/logger';

const logger = new Logger('FILTERED_RESTAURANTS_CAROUSEL', 'ERROR');

export default function useFilteredRestaurantsCarousel({
  regionId,
  mode,
  enabled = true,
  availableTodayDate,
}: {
  regionId?: number;
  mode: FilteredRestaurantsCarouselMode;
  enabled?: boolean;
  availableTodayDate?: string;
}) {
  const [restaurants, setRestaurants] = useState<FilteredRestaurant[]>([]);
  const [isLoading, setIsLoading] = useState(() => regionId != null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;

    if (regionId == null) {
      setRestaurants([]);
      setIsLoading(false);
      setHasError(false);
      return undefined;
    }

    const abortController = new AbortController();
    const { signal } = abortController;

    setIsLoading(true);
    setHasError(false);

    (async () => {
      try {
        const results = await getFilteredRestaurantsCarousel({
          regionId,
          mode,
          abortSignal: signal,
          availableTodayDate,
        });

        if (signal.aborted) {
          return;
        }

        setRestaurants(results);
      } catch (error) {
        if (signal.aborted) {
          return;
        }

        setRestaurants([]);
        setHasError(true);
        logger.error('Failed to fetch filtered restaurants carousel', error);
      } finally {
        if (!signal.aborted) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      abortController.abort();
    };
  }, [regionId, mode, enabled, availableTodayDate]);

  return { restaurants, isLoading, hasError };
}
